import {
	readPcmPeakLevels,
	smoothWaveformTimelineFrames,
	WAVEFORM_ANALYSER_SMOOTHING,
} from "./waveform-analyser-levels";

export const WAVEFORM_TIMELINE_FPS = 60;

export interface WaveformLevelTimeline {
	frames: number[][];
	durationSeconds: number;
	frameStepSeconds: number;
}

let sharedDecodeContext: AudioContext | null = null;

function getDecodeContext(): AudioContext {
	if (!sharedDecodeContext) {
		sharedDecodeContext = new AudioContext();
	}
	return sharedDecodeContext;
}

function createOfflineDecodeContext():
	| OfflineAudioContext
	| AudioContext
	| null {
	const Offline =
		globalThis.OfflineAudioContext ??
		(
			globalThis as typeof globalThis & {
				webkitOfflineAudioContext?: typeof OfflineAudioContext;
			}
		).webkitOfflineAudioContext;
	if (typeof Offline === "function") {
		return new Offline(1, 1, 44_100);
	}
	if (typeof AudioContext === "function") {
		return getDecodeContext();
	}
	return null;
}

export async function decodeAudioBufferFromUrl(
	url: string,
): Promise<AudioBuffer | null> {
	try {
		const response = await fetch(url, { mode: "cors", credentials: "omit" });
		if (!response.ok) {
			return null;
		}
		const arrayBuffer = await response.arrayBuffer();
		const context = createOfflineDecodeContext();
		if (context == null) {
			return null;
		}
		return await context.decodeAudioData(arrayBuffer.slice(0));
	} catch {
		return null;
	}
}

/**
 * Bake playhead-synced bar envelopes from PCM. Do **not** use a realtime
 * AnalyserNode + AudioContext.resume() here — topic preload runs before any
 * click, so that path yields silent identical frames (looks like static seed).
 */
export async function buildWaveformLevelTimeline(
	buffer: AudioBuffer,
	barCount: number,
): Promise<WaveformLevelTimeline> {
	const frameStepSeconds = 1 / WAVEFORM_TIMELINE_FPS;
	const frameStepSamples = Math.max(
		1,
		Math.floor(frameStepSeconds * buffer.sampleRate),
	);
	const frameCount = Math.max(1, Math.ceil(buffer.length / frameStepSamples));
	const channelData = buffer.getChannelData(0);
	const rawFrames: number[][] = [];

	for (let frame = 0; frame < frameCount; frame += 1) {
		const startSample = frame * frameStepSamples;
		const lengthSamples = Math.min(
			frameStepSamples,
			buffer.length - startSample,
		);
		if (lengthSamples <= 0) {
			break;
		}
		rawFrames.push(
			readPcmPeakLevels(
				channelData,
				startSample,
				lengthSamples,
				barCount,
			),
		);
	}

	return {
		frames: smoothWaveformTimelineFrames(
			rawFrames,
			WAVEFORM_ANALYSER_SMOOTHING,
		),
		durationSeconds: buffer.duration,
		frameStepSeconds,
	};
}

/** True when every frame sits on the PCM/analyser floor (failed bake / silence). */
export function isSilentWaveformTimeline(
	timeline: WaveformLevelTimeline | null | undefined,
): boolean {
	if (timeline == null || timeline.frames.length === 0) {
		return true;
	}
	return timeline.frames.every((frame) =>
		frame.every((level) => level <= 0.16),
	);
}

/** Deterministic lookup: bar heights at `timeSeconds` on the precomputed grid. */
export function sampleWaveformLevelTimeline(
	timeline: WaveformLevelTimeline,
	timeSeconds: number,
): number[] {
	const { frames, durationSeconds, frameStepSeconds } = timeline;
	if (frames.length === 0) {
		return [];
	}

	const clampedTime = Math.max(0, Math.min(durationSeconds, timeSeconds));
	const exactIndex = clampedTime / frameStepSeconds;
	const lowerIndex = Math.floor(exactIndex);
	const upperIndex = Math.min(frames.length - 1, lowerIndex + 1);
	const blend = exactIndex - lowerIndex;
	const lowerFrame = frames[lowerIndex] ?? frames[0];
	const upperFrame = frames[upperIndex] ?? lowerFrame;

	return lowerFrame.map((lowerLevel, index) => {
		const upperLevel = upperFrame[index] ?? lowerLevel;
		return lowerLevel + (upperLevel - lowerLevel) * blend;
	});
}
